import React, { useState } from 'react';
import styled from 'styled-components';
import { SatelliteArchiveCostDTO, SatelliteArchiveImageryDTO } from '../../../../api/api-supplier';
import Analytics, { ConversionEvent } from '../../../../lib/user-analytics';
import PriceWithCurrency from '../../../Shared/price-with-currency';
import { Button, Content, Title } from '../satellite-drawer-styles';
import SatelliteOrderVoucherInput from './satellite-order-voucher-input';

interface SatelliteOrderPurchaseDetailsProps {
    isLoading: boolean;
    isVoucherVisible: boolean;
    onConfirm: () => void;
    onEnterVoucher: (voucher: string) => void;
    selectedFeatures: SatelliteArchiveImageryDTO[];
    cost: SatelliteArchiveCostDTO | undefined;
    error: Error | undefined;
}

const FormattedPriceWithCurrency: React.FC<{ currency: string; amount: number }> = (props) => {
    const audUS = props.currency.toLowerCase() === 'aud';
    return (
        <FormattedPrice>
            <PriceLabel>
                <PriceWithCurrency currency={props.currency} value={props.amount} />
            </PriceLabel>
            {audUS && <GST> Inc GST</GST>}
        </FormattedPrice>
    );
};

const SatelliteOrderPurchaseDetails = (props: SatelliteOrderPurchaseDetailsProps) => {
    const { isLoading, cost } = props;
    const [showVoucherInput, setShowVoucherInput] = useState(false);
    const areaInKmString = cost ? `${cost.areaInKm}km²` : '--km²';
    const percentageCoveredString = cost ? `${Math.round((cost.effectiveAreaInKm / cost.areaInKm) * 100)}%` : '--%';

    return (
        <Content>
            <Title>Your order summary</Title>
            <CollectionDetails>
                <CollectionItems>
                    <p>Total Area</p>
                    <p>{areaInKmString}</p>
                </CollectionItems>
                <CollectionCostDivider />
                <CollectionItems>
                    <p>Pixel Resolution</p>
                    <p>{props.selectedFeatures[0].resolutionInCm}cm/pixel</p>
                </CollectionItems>
                <CollectionCostDivider />
                <CollectionItems>
                    <p>Number of scenes</p>
                    <p>{props.selectedFeatures.length}</p>
                </CollectionItems>
                <CollectionCostDivider />
                <CollectionItems>
                    <p>Total Coverage</p>
                    <p>{percentageCoveredString}</p>
                </CollectionItems>
                <CollectionCostDivider />
                {!isLoading && cost && !!cost.voucherValueUsed && (
                    <>
                        <CollectionItems>
                            <p>Voucher Value</p>
                            <p>
                                <PriceWithCurrency currency={cost.currency} value={cost.voucherValueUsed} />
                            </p>
                        </CollectionItems>
                        <CollectionCostDivider />
                    </>
                )}
                <CollectionItems>
                    <p>Total Price</p>
                    <CollectionCost>
                        {!isLoading && cost && cost.currency ? (
                            <FormattedPriceWithCurrency currency={cost.currency} amount={cost.totalPrice} />
                        ) : (
                            <NoCost>$---</NoCost>
                        )}
                    </CollectionCost>
                </CollectionItems>
            </CollectionDetails>
            <CollectionButtonContainer>
                <React.Fragment>
                    {props.isVoucherVisible ? (
                        <VoucherButton onClick={() => setShowVoucherInput(true)}>I HAVE A VOUCHER</VoucherButton>
                    ) : null}

                    <Button
                        onClick={() => {
                            props.onConfirm();
                            Analytics.Event('Satellite - SkyMap50 - New Collect', 'Clicked to confirm order');
                            Analytics.Conversion(ConversionEvent.CONFIRM_ORDER);
                        }}
                        disabled={isLoading || !cost}
                    >
                        Confirm order
                    </Button>
                </React.Fragment>
            </CollectionButtonContainer>

            <SatelliteOrderVoucherInput
                isLoading={isLoading}
                toggle={() => setShowVoucherInput(!showVoucherInput)}
                isOpen={showVoucherInput}
                handleSubmitCoupon={props.onEnterVoucher}
                couponError={props.error ? `${props.error.message.replace('400: ', '')}` : ''}
                voucherDetails={cost}
            />
        </Content>
    );
};

export default SatelliteOrderPurchaseDetails;

const CollectionDetails = styled.div`
    padding: 0px 12px;
    font-size: 16px;
`;

const CollectionItems = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding-top: 15px;
    padding-bottom: 5px;

    p {
        color: white;
    }

    p:nth-child(2) {
        color: rgba(255, 255, 255, 0.6);
        display: flex;
        flex-direction: row;
    }
`;

const CollectionCost = styled.div`
    display: flex;
    flex-direction: row;

    div {
        color: #eed926 !important;
        font-size: 16px !important;
    }

    div:nth-child(2) {
        padding-left: 4px;
    }
`;

const CollectionButtonContainer = styled.div`
    border-top: 1px solid rgba(255, 255, 255, 0.3);
    display: flex;
    flex-direction: row;

    button {
        margin-bottom: 10px !important;
        margin-top: 10px !important;
    }
`;

const CollectionCostDivider = styled.div`
    border-top: 1px solid rgb(255, 255, 255, 0.3);
`;

const GST = styled.div`
    font-size: 1.2rem;
    margin-left: 5px;
`;
const NoCost = styled.div`
    color: #eed926;
    font-size: 16px;
`;

const FormattedPrice = styled.div`
    display: flex;
    flex-direction: row;
`;

const PriceLabel = styled.div`
    font-size: 16px;
`;

const VoucherButton = styled.button`
    background-color: transparent !important;
    color: white !important;
    border: 1px solid white !important;
    margin-top: 30px;
    margin-bottom: 60px;
    padding-top: 8px;
    padding-bottom: 8px;
    padding-left: 25px;
    padding-right: 25px;
    font-size: 1rem;
    border-radius: 4px;
    margin-left: 35px;
`;
